import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { ExternalLink, Filter, MessageSquare } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Select } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { fmtCompactRelative, fmtNumber } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { ConnectionSummary, ProviderKey } from "@/types/support_connection"

// AgentConvos#index — operator-facing convo list with source / provider /
// connection filtering. The native vs. imported split is the headline pivot:
// `?source=imported` filters to rows with `external_id IS NOT NULL`, then
// the provider + connection drilldowns narrow further.
//
// Wave 5A's controller serializes these props; this page is intentionally
// thin (single-table list) — the existing Tickets index covers richer
// queue/triage UX. Native+imported-merged view is a future wave.

const BASE = "/portal/agent_convos"

type SourceFilter = "all" | "native" | "imported"

type ConvoRow = {
  id:                              number
  uuid:                            string | null
  subject:                         string
  state:                           string
  external_id:                     string | null
  external_url:                    string | null
  provider:                        ProviderKey | null
  provider_display_name:           string | null
  connection_id:                   number | null
  connection_name:                 string | null
  requester_email:                 string | null
  requester_name:                  string | null
  agent_name:                      string | null
  last_message_at:                 string | null
  created_at:                      string
}

type Filters = {
  source:                          SourceFilter
  provider:                        ProviderKey | ""
  support_platform_connection_id:  number | null
  state:                           string
  q:                               string
}

type Props = {
  convos:              ConvoRow[]
  moreAvailable:       boolean
  filters:             Filters
  availableProviders:  Array<{ key: ProviderKey; display_name: string }>
  connections:         ConnectionSummary[]
  states:              string[]
}

const SOURCE_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: "all",      label: "All" },
  { value: "native",   label: "Valet-native" },
  { value: "imported", label: "Imported" },
]

export default function AgentConvosIndex({
  convos,
  moreAvailable,
  filters,
  availableProviders,
  connections,
  states,
}: Props) {
  const [searchValue, setSearchValue] = useState(filters.q)

  const providerOptions = useMemo(
    () => [
      { value: "", label: "All providers" },
      ...availableProviders.map((p) => ({ value: p.key, label: p.display_name })),
    ],
    [availableProviders],
  )

  // Connection options scope to the picked provider when one is set.
  const connectionOptions = useMemo(() => {
    const filtered = filters.provider
      ? connections.filter((c) => c.provider === filters.provider)
      : connections
    return [
      { value: 0, label: "All connections" },
      ...filtered.map((c) => ({ value: c.id, label: c.name ?? c.provider_display_name })),
    ]
  }, [connections, filters.provider])

  const stateOptions = useMemo(
    () => [
      { value: "", label: "All states" },
      ...states.map((s) => ({ value: s, label: s.replace(/_/g, " ") })),
    ],
    [states],
  )

  function visit(updates: Partial<Filters>) {
    const next: Record<string, string | number | null> = {
      source:    updates.source                          ?? filters.source,
      provider:  updates.provider                        ?? filters.provider,
      support_platform_connection_id:
                 updates.support_platform_connection_id  ?? filters.support_platform_connection_id,
      state:     updates.state                           ?? filters.state,
      q:         updates.q                               ?? filters.q,
    }
    // Clear provider/connection when switching back to native.
    if (next.source === "native") {
      next.provider = ""
      next.support_platform_connection_id = null
    }
    // Clear connection when changing provider.
    if (updates.provider !== undefined && updates.provider !== filters.provider) {
      next.support_platform_connection_id = null
    }
    const params = new URLSearchParams()
    if (next.source && next.source !== "all") params.set("source", String(next.source))
    if (next.provider) params.set("provider", String(next.provider))
    if (next.support_platform_connection_id) params.set("support_platform_connection_id", String(next.support_platform_connection_id))
    if (next.state) params.set("state", String(next.state))
    if (next.q) params.set("q", String(next.q))
    router.get(`${BASE}?${params.toString()}`, {}, { preserveScroll: true, preserveState: true })
  }

  const subtitle = useMemo(() => {
    if (convos.length === 0) return "No conversations match the current filters."
    return `${fmtNumber(convos.length)} conversation${convos.length === 1 ? "" : "s"}${
      moreAvailable ? "+ — narrow filters to see more" : ""
    }`
  }, [convos.length, moreAvailable])

  return (
    <AppShell>
      <PageHeader eyebrow="Conversations" title="Agent conversations" subtitle={subtitle} />

      {/* Filter bar */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
          {/* Source pills */}
          <div className="flex gap-1.5">
            {SOURCE_OPTIONS.map((opt) => (
              <SourcePill
                key={opt.value}
                active={filters.source === opt.value}
                onClick={() => visit({ source: opt.value })}
                label={opt.label}
              />
            ))}
          </div>

          {/* Provider — only when imported */}
          {filters.source === "imported" && (
            <div className="min-w-[180px]">
              <label className="block text-[10px] uppercase tracking-wide text-app-muted">Provider</label>
              <Select<string>
                value={
                  filters.provider
                    ? providerOptions.find((o) => o.value === filters.provider) ?? providerOptions[0]
                    : providerOptions[0]
                }
                options={providerOptions}
                onChange={(o) => visit({ provider: (o?.value ?? "") as ProviderKey | "" })}
                isSearchable={false}
              />
            </div>
          )}

          {/* Connection — only when provider picked */}
          {filters.source === "imported" && filters.provider && (
            <div className="min-w-[200px]">
              <label className="block text-[10px] uppercase tracking-wide text-app-muted">Connection</label>
              <Select<number>
                value={
                  filters.support_platform_connection_id
                    ? connectionOptions.find((o) => o.value === filters.support_platform_connection_id) ??
                      connectionOptions[0]
                    : connectionOptions[0]
                }
                options={connectionOptions}
                onChange={(o) =>
                  visit({
                    support_platform_connection_id: o && o.value !== 0 ? o.value : null,
                  })
                }
                isSearchable={false}
              />
            </div>
          )}

          {/* State filter (works for both sources) */}
          <div className="min-w-[160px]">
            <label className="block text-[10px] uppercase tracking-wide text-app-muted">State</label>
            <Select<string>
              value={
                filters.state
                  ? stateOptions.find((o) => o.value === filters.state) ?? stateOptions[0]
                  : stateOptions[0]
              }
              options={stateOptions}
              onChange={(o) => visit({ state: o?.value ?? "" })}
              isSearchable={false}
            />
          </div>

          {/* Search */}
          <div className="min-w-[220px] flex-1">
            <label className="block text-[10px] uppercase tracking-wide text-app-muted">Search</label>
            <Input
              type="search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={() => searchValue !== filters.q && visit({ q: searchValue })}
              onKeyDown={(e) => {
                if (e.key === "Enter") visit({ q: searchValue })
              }}
              placeholder="Subject, requester…"
              className="w-full"
            />
          </div>
        </div>

        {/* Convo rows */}
        {convos.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
            <Filter size={20} className="text-app-muted" />
            <p className="text-sm text-app-muted">No conversations in this view.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {convos.map((convo) => (
              <li key={convo.id}>
                <ConvoListRow convo={convo} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </AppShell>
  )
}

function SourcePill({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-control border px-3 py-1.5 text-xs font-medium transition",
        active
          ? "border-brand-red/50 bg-brand-redSoft text-brand-red ring-1 ring-brand-red/20"
          : "border-border bg-white/5 text-app-text hover:bg-white/10",
      )}
    >
      {label}
    </button>
  )
}

function ConvoListRow({ convo }: { convo: ConvoRow }) {
  const isImported = !!convo.external_id
  return (
    <a
      href={convo.uuid ? `/tickets/${convo.uuid}` : "#"}
      className="flex items-center gap-4 px-5 py-3 transition hover:bg-white/5"
    >
      <MessageSquare size={14} className="shrink-0 text-app-muted" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-app-text">{convo.subject || "(no subject)"}</span>
          <Badge tone="neutral">{convo.state.replace(/_/g, " ")}</Badge>
          {isImported && (
            <Badge tone="success">
              Imported{convo.provider_display_name ? ` from ${convo.provider_display_name}` : ""}
            </Badge>
          )}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-app-muted">
          {convo.requester_name || convo.requester_email || "anonymous"}
          {convo.connection_name && (
            <>
              {" · "}
              <span className="font-mono">{convo.connection_name}</span>
            </>
          )}
          {convo.agent_name && <> · {convo.agent_name}</>}
        </div>
      </div>

      <div className="hidden text-right text-[11px] text-app-muted sm:block">
        {fmtCompactRelative(convo.last_message_at ?? convo.created_at)}
      </div>

      {isImported && convo.external_url && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (convo.external_url) window.open(convo.external_url, "_blank", "noopener")
          }}
          className="ml-2 rounded-control border border-border bg-white/5 p-1.5 text-app-muted transition hover:bg-white/10 hover:text-app-text"
          aria-label="Open in source platform"
          title="Open in source platform"
        >
          <ExternalLink size={12} />
        </button>
      )}
    </a>
  )
}
